<template>
  <div class="column-toggle-wrapper" v-on-clickaway="closeColumnDropdown">
    <woot-button
      class="clear column-toggle-btn"
      color-scheme="secondary"
      @click="toggleColumnDropdown"
    >
      <svg width="20" height="20" viewBox="0 0 34 34" fill="currentColor" style="margin-right: 8px;">
        <path d="M28.708,17.309l-1.082-.625a10.105,10.105,0,0,0,0-1.5l1.082-.625a3.983,3.983,0,0,0,1.461-5.444l-1.328-2.3A3.993,3.993,0,0,0,23.4,5.352l-1.085.627a11.833,11.833,0,0,0-1.3-.745V3.983A3.983,3.983,0,0,0,17.031,0H14.375a3.983,3.983,0,0,0-3.983,3.983V5.234a11.833,11.833,0,0,0-1.3.752L8.01,5.359A3.993,3.993,0,0,0,2.566,6.82l-1.328,2.3A3.983,3.983,0,0,0,2.7,14.565l1.082.625a10.106,10.106,0,0,0,0,1.5L2.7,17.316A3.983,3.983,0,0,0,1.238,22.76l1.328,2.3A3.993,3.993,0,0,0,8.01,26.522l1.085-.627a11.833,11.833,0,0,0,1.3.738v1.251a3.983,3.983,0,0,0,3.983,3.983h2.656a3.983,3.983,0,0,0,3.983-3.983V26.633a11.835,11.835,0,0,0,1.3-.752l1.085.627a3.983,3.983,0,0,0,5.444-1.461l1.328-2.3a3.983,3.983,0,0,0-1.461-5.437Zm-13,5.263a6.639,6.639,0,1,1,6.639-6.639A6.639,6.639,0,0,1,15.7,22.573Z"/>
      </svg>
      Columns
    </woot-button>
    
    <div v-if="showColumnDropdown" class="column-dropdown">
      <div class="column-dropdown-header">
        <span>Show/Hide & Reorder Columns</span>
        <button 
          class="reset-btn"
          @click="resetColumns"
        >
          Reset
        </button>
      </div>
      <draggable
        v-model="columnOrder"
        class="column-list"
        handle=".drag-handle"
        @end="onColumnReorder"
        :animation="200"
        ghost-class="column-ghost"
      >
        <div
          v-for="column in orderedColumnsWithDetails"
          :key="column.key"
          class="column-item"
          :class="{ 'column-hidden': !visibleColumns.includes(column.key) }"
        >
          <div class="drag-handle" title="Drag to reorder">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <circle cx="6" cy="4" r="1.5"/>
              <circle cx="10" cy="4" r="1.5"/>
              <circle cx="6" cy="8" r="1.5"/>
              <circle cx="10" cy="8" r="1.5"/>
              <circle cx="6" cy="12" r="1.5"/>
              <circle cx="10" cy="12" r="1.5"/>
            </svg>
          </div>
          <input
            type="checkbox"
            :checked="visibleColumns.includes(column.key)"
            @change="toggleColumn(column.key)"
            :disabled="column.key === 'name' || column.key === 'actions'"
          />
          <span class="column-title">{{ column.title }}</span>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import draggable from 'vuedraggable';
import WootButton from 'dashboard/components/ui/WootButton.vue';
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from '../../../../featureFlags';

export default {
  name: 'ContactColumnManager',
  components: {
    draggable,
    WootButton,
  },
  mixins: [clickaway],
  data() {
    return {
      showColumnDropdown: false,
      visibleColumns: [],
      columnOrder: [],
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      allAttributes: 'attributes/getAttributes',
      allAttributes: 'attributes/getAttributes',
    }),
    isMaacClientBranchFeatureEnabled() {
      return this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.MAAC_CLIENT_BRANCH);
    },
    customAttributeColumns() {
      const contactAttributes = this.allAttributes.filter(
        attr => attr.attribute_model === 'contact_attribute'
      ) || [];
      return contactAttributes.map(attr => ({
        key: `custom_${attr.attribute_key}`,
        title: attr.attribute_display_name || attr.attribute_key,
        isCustomAttribute: true,
        attributeKey: attr.attribute_key,
        attributeType: attr.attribute_display_type,
      }));
    },
    allColumns() {
      const baseColumns = [
        {
          key: 'name',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.NAME'),
        },
        {
          key: 'email',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.EMAIL_ADDRESS'),
        },
        {
          key: 'phone_number',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.PHONE_NUMBER'),
        },
        {
          key: 'created_at',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.CREATED_AT'),
        },
        {
          key: 'assigned_to',
          title: 'Assigned To',
        },
        {
          key: 'source',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.SOURCE'),
        },
        // Conditionally show Branch OR Company based on feature flag
        ...(this.isMaacClientBranchFeatureEnabled ? [{
          key: 'branch',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.BRANCH'),
        }] : []),
        ...(this.isMaacClientBranchFeatureEnabled ? [] : [{
          key: 'company',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.COMPANY'),
        }]),
        {
          key: 'city',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.CITY'),
        },
        {
          key: 'board',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.BOARD'),
        },
        {
          key: 'status',
          title: 'PIPELINE',
        },
        {
          key: 'state',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.STATE'),
        },
        {
          key: 'country',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.COUNTRY'),
        },
        {
          key: 'remark',
          title: 'Social',
        },
      ];

      return [
        ...baseColumns,
        ...this.customAttributeColumns,
        {
          key: 'actions',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.ACTIONS'),
        },
      ];
    },
    orderedColumnsWithDetails() {
      return this.columnOrder
        .map(key => this.allColumns.find(col => col.key === key))
        .filter(col => col !== undefined);
    },
  },
  mounted() {
    this.initializeVisibleColumns();
  },
  watch: {
    customAttributeColumns: {
      handler(newVal, oldVal) {
        if (newVal.length > 0 && (!oldVal || oldVal.length !== newVal.length)) {
          this.$nextTick(() => {
            this.initializeVisibleColumns();
          });
        }
      },
      deep: true,
    },
  },
  watch: {
    customAttributeColumns: {
      handler(newVal, oldVal) {
        // When custom attributes are loaded or changed, reinitialize to include them
        if (newVal.length > 0 && (!oldVal || oldVal.length !== newVal.length)) {
          this.$nextTick(() => {
            this.initializeVisibleColumns();
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    getDefaultColumns() {
      // Build default columns based on feature flag
      const baseColumns = [
        'name',
        'email',
        'phone_number',
        'created_at',
        'assigned_to',
        'source',
      ];
      
      // Conditionally add branch OR company
      const conditionalColumn = this.isMaacClientBranchFeatureEnabled ? 'branch' : 'company';
      
      const remainingColumns = [
        'city',
        'board',
        'status',
        'state',
        'country',
        'remark',
        'actions',
      ];
      
      return [...baseColumns, conditionalColumn, ...remainingColumns];
    },
    /** Full column order for the popup (standard + custom_* before actions). */
    getDefaultColumnOrder() {
      const standard = this.getDefaultColumns();
      const actionsIndex = standard.indexOf('actions');
      const beforeActions =
        actionsIndex >= 0 ? standard.slice(0, actionsIndex) : standard.filter(k => k !== 'actions');
      const customKeys = this.customAttributeColumns.map(c => c.key);
      return [...beforeActions, ...customKeys, 'actions'];
    },
    /** Default visible keys in the table: standard columns only; custom attributes stay off until enabled. */
    getDefaultVisibleColumns() {
      return [...this.getDefaultColumns()];
    },
    /**
     * Swaps 'company' ↔ 'branch' based on feature flag
     * Preserves exact position and visibility state
     */
    syncConditionalColumn(savedColumns) {
      const currentColumn = this.isMaacClientBranchFeatureEnabled ? 'branch' : 'company';
      const oldColumn = this.isMaacClientBranchFeatureEnabled ? 'company' : 'branch';
      
      // Simply replace old column with current column wherever it exists
      return savedColumns.map(col => col === oldColumn ? currentColumn : col);
    },
    initializeVisibleColumns() {
      const defaultCols = this.getDefaultColumns();
      
      const savedVisible = localStorage.getItem('contactTableVisibility');
      if (savedVisible) {
        this.visibleColumns = this.syncConditionalColumn(JSON.parse(savedVisible));
      } else {
        this.visibleColumns = [...this.getDefaultVisibleColumns()];
      }

      const savedOrder = localStorage.getItem('contactTableOrder');
      if (savedOrder) {
        this.columnOrder = this.syncConditionalColumn(JSON.parse(savedOrder));
      } else {
        this.columnOrder = [...defaultCols];
      }

      // Add any new custom attribute columns that aren't in columnOrder yet
      const allColumnKeys = this.allColumns.map(col => col.key);
      const missingColumns = allColumnKeys.filter(key => !this.columnOrder.includes(key));
      
      if (missingColumns.length > 0) {
        // Insert missing columns before 'actions' column
        const actionsIndex = this.columnOrder.indexOf('actions');
        if (actionsIndex > -1) {
          this.columnOrder.splice(actionsIndex, 0, ...missingColumns);
        } else {
          this.columnOrder.push(...missingColumns);
        }
      }

      localStorage.setItem('contactTableVisibility', JSON.stringify(this.visibleColumns));
      localStorage.setItem('contactTableOrder', JSON.stringify(this.columnOrder));

      this.emitColumnConfig();
    },
    toggleColumnDropdown() {
      this.showColumnDropdown = !this.showColumnDropdown;
    },
    closeColumnDropdown() {
      this.showColumnDropdown = false;
    },
    toggleColumn(columnKey) {
      if (columnKey === 'name' || columnKey === 'actions') {
        return;
      }

      const index = this.visibleColumns.indexOf(columnKey);
      if (index > -1) {
        this.visibleColumns.splice(index, 1);
      } else {
        this.visibleColumns.push(columnKey);
      }

      localStorage.setItem(
        'contactTableVisibility',
        JSON.stringify(this.visibleColumns)
      );

      this.emitColumnConfig();
    },
    resetColumns() {
      this.visibleColumns = [...this.getDefaultVisibleColumns()];
      this.columnOrder = [...this.getDefaultColumnOrder()];

      localStorage.setItem(
        'contactTableVisibility',
        JSON.stringify(this.visibleColumns)
      );
      localStorage.setItem(
        'contactTableOrder',
        JSON.stringify(this.columnOrder)
      );

      this.emitColumnConfig();
    },
    onColumnReorder() {
      localStorage.setItem(
        'contactTableOrder',
        JSON.stringify(this.columnOrder)
      );

      this.emitColumnConfig();
    },
    emitColumnConfig() {
      this.$emit('column-config-change', {
        visibleColumns: this.visibleColumns,
        columnOrder: this.columnOrder,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.column-toggle-wrapper {
  position: relative;
  display: inline-block;
}

.column-toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.column-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 250px;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 1!important;
  max-height: 400px;
  overflow-y: auto;
}

.column-dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  font-size: 0.875rem;
  color: #111827;
}

.reset-btn {
  padding: 0.25rem 0.75rem;
  background-color: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  cursor: pointer;
  color: #374151;
  transition: all 0.2s;

  &:hover {
    background-color: #e5e7eb;
  }
}

.column-list {
  padding: 0.5rem 0;
  min-height: 100px;
}

.column-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #374151;
  transition: all 0.2s;
  border-left: 3px solid transparent;
  cursor: move;

  &:hover {
    background-color: #f9fafb;
    border-left-color: #3b82f6;
  }

  .drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    color: #6b7280;
    transition: all 0.2s;
    padding: 0.25rem;
    border-radius: 0.25rem;
    min-width: 24px;
    min-height: 24px;

    svg {
      color: #6b7280;
      transition: color 0.2s;
    }

    &:hover {
      background-color: #f3f4f6;
      
      svg {
        color: #3b82f6;
      }
    }

    &:active {
      cursor: grabbing;
      background-color: #e5e7eb;
      
      svg {
        color: #2563eb;
      }
    }
  }

  input[type='checkbox'] {
    cursor: pointer;
    width: 1rem;
    height: 1rem;
    margin: 0;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .column-title {
    flex: 1;
    user-select: none;
  }

  &.column-hidden {
    opacity: 0.5;
    
    .column-title {
      text-decoration: line-through;
      color: #9ca3af;
    }
  }
}

.column-ghost {
  opacity: 0.5;
  background-color: #dbeafe;
  border: 2px dashed #3b82f6;
  border-radius: 0.375rem;
}

// Dark mode support
.dark {
  .column-dropdown {
    background-color: #1e293b;
    border-color: #475569;
  }
  
  .column-dropdown-header {
    border-bottom-color: #475569;
    color: #f1f5f9;
  }
  
  .reset-btn {
    background-color: #334155;
    border-color: #475569;
    color: #e2e8f0;
    
    &:hover {
      background-color: #475569;
    }
  }
  
  .column-item {
    color: #cbd5e1;
    
    &:hover {
      background-color: #334155;
      border-left-color: #3b82f6;
    }

    .drag-handle {
      svg {
        color: #94a3b8;
      }

      &:hover {
        background-color: #475569;
        
        svg {
          color: #3b82f6;
        }
      }

      &:active {
        background-color: #64748b;
        
        svg {
          color: #2563eb;
        }
      }
    }

    &.column-hidden {
      opacity: 0.5;
      
      .column-title {
        text-decoration: line-through;
        color: #64748b;
      }
    }
  }

  .column-ghost {
    background-color: #1e3a5f;
    border-color: #3b82f6;
  }
}
</style>
